import { readInlineData, graphql } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import { BID_RESPONSE_ERRORS, BidErrorType } from '../../constants';

import type { bidModalTrackingHelpers_item$key } from './__generated__/bidModalTrackingHelpers_item.graphql';

const itemFragment = graphql`
    fragment bidModalTrackingHelpers_item on Item @inline {
        serviceId
        relevantAuctionSolution(page: pdp) {
            minNextBidAmount {
                amount
            }
        }
    }
`;

export const TRACKING_LABEL_BID_CLICK = 'bid click - auction PDP';

export function trackAuctionEvent({
    action,
    label,
    itemId,
    value,
    isInteractiveEvent = false,
}: {
    action: string;
    label: string;
    itemId?: string;
    value?: string;
    isInteractiveEvent?: boolean;
}): void {
    trackEvent({
        category: 'auction',
        action,
        label,
        itemId,
        value,
        isInteractiveEvent,
    });
}

export function trackAuctionBidClick({
    item: itemRef,
}: {
    item: bidModalTrackingHelpers_item$key;
}): void {
    const item = readInlineData(itemFragment, itemRef);

    const minBidAmount = item?.relevantAuctionSolution?.minNextBidAmount?.amount || 0;

    trackAuctionEvent({
        action: 'auction bid entry',
        label: 'auction PDP - bid click',
        itemId: item?.serviceId || '',
        value: String(minBidAmount),
        isInteractiveEvent: true,
    });
}

export function trackAuctionBidConfirmClick({
    bidAmount,
    itemId,
}: {
    bidAmount: number;
    itemId: string;
}): void {
    trackAuctionEvent({
        action: 'auction bid interaction',
        label: 'auction PDP - confirm bid click',
        itemId,
        value: String(bidAmount),
        isInteractiveEvent: true,
    });
}

function getAuctionTimeLeftInMinutes(endDate: string): number {
    const diff = Math.round((Date.parse(endDate) - Date.now()) / 1000 / 60);
    return Math.max(diff, 0);
}

export function trackAuctionBidTimeLeft({ endDate }: { endDate?: string | null }): void {
    const minutesLeft = getAuctionTimeLeftInMinutes(endDate || '');
    trackAuctionEvent({
        action: 'place bid - auction time left',
        label: String(minutesLeft),
    });
}

export function trackAuctionBidComplete({
    bidAmount,
    bidErrorType,
    itemId,
}: {
    bidAmount: number;
    bidErrorType: BidErrorType | null;
    itemId: string;
}): void {
    let label = '';
    switch (true) {
        case bidErrorType === null:
            label = 'successful';
            break;
        case bidErrorType === BID_RESPONSE_ERRORS.CLOSE_TO_RESERVE:
            label = 'successful - reserve not met but close';
            break;
        case bidErrorType === BID_RESPONSE_ERRORS.RESERVE_NOT_MET:
            label = 'successful - reserve not met';
            break;
        case bidErrorType === BID_RESPONSE_ERRORS.OUTBID:
        case bidErrorType === BID_RESPONSE_ERRORS.OUTBID_RESERVE_NOT_MET:
            label = 'successful but auto outbid';
            break;
    }

    trackAuctionEvent({
        action: 'auction bid complete',
        label,
        itemId,
        value: String(bidAmount),
    });
}

export function trackAppDownloadCTA({ action }: { action: string }): void {
    trackEvent({
        action,
        label: 'bid confirmation',
        category: 'promo interaction',
    });
}
